import { useEffect, useState } from 'react'
import { Link, Outlet } from 'react-router-dom'
import Header from '../navigation/Header'
import Sidebar from '../navigation/Sidebar'
import { ShieldExclamationIcon, XMarkIcon } from '@heroicons/react/24/outline'
import api from '../../services/api'
import RoiCalculatorModal from '../RoiCalculatorModal'
import { SalesEnablementContext } from '../../contexts/SalesEnablementContext'
import { useSessionTimeout } from '../../hooks/useSessionTimeout'
import EnhancedAuditTracker from '../audit/EnhancedAuditTracker'

const DEFAULT_SESSION_TIMEOUT_MINUTES = 60

function MFABanner() {
  const [show, setShow] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    api.get('/mfa/status')
      .then(({ data }) => {
        if (data.org_requires_mfa && !data.mfa_enabled) setShow(true)
      })
      .catch(() => {})
  }, [])

  if (!show || dismissed) return null

  return (
    <div className="flex items-center gap-3 bg-amber-50 border-b border-amber-200 px-4 py-2.5">
      <ShieldExclamationIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
      <p className="flex-1 text-sm text-amber-800">
        <span className="font-semibold">MFA required by your organization.</span>{' '}
        Enable two-factor authentication to keep your account secure.{' '}
        <Link to="/settings" className="underline font-medium hover:text-amber-900">
          Set up MFA in Settings
        </Link>
      </p>
      <button
        onClick={() => setDismissed(true)}
        className="text-amber-500 hover:text-amber-700 flex-shrink-0"
        aria-label="Dismiss"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  )
}

export default function MainLayout() {
  const [roiCalculatorOpen, setRoiCalculatorOpen] = useState(false)
  // Enforce a safe client-side fallback even when the settings request is
  // unavailable. The server-provided organization/platform policy replaces it
  // as soon as that request succeeds.
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(DEFAULT_SESSION_TIMEOUT_MINUTES)

  const openRoiCalculator = () => setRoiCalculatorOpen(true)
  const closeRoiCalculator = () => setRoiCalculatorOpen(false)

  useEffect(() => {
    const loadSessionPolicy = () => api.get('/admin/settings/session-timeout')
      .then(({ data }) => {
        const effectiveTimeout = data?.effective_minutes
          ?? data?.session_timeout_minutes
          ?? data?.platform_default_minutes
        if (effectiveTimeout) setSessionTimeoutMinutes(effectiveTimeout)
      })
      .catch(() => {})

    void loadSessionPolicy()
    const interval = window.setInterval(loadSessionPolicy, 5 * 60 * 1000)
    window.addEventListener('session-settings:changed', loadSessionPolicy)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('session-settings:changed', loadSessionPolicy)
    }
  }, [])

  useSessionTimeout(sessionTimeoutMinutes)

  return (
    <SalesEnablementContext.Provider value={{ openRoiCalculator }}>
      <EnhancedAuditTracker />
      <div className="flex h-screen bg-gray-50">
        <Sidebar onOpenRoi={openRoiCalculator} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <MFABanner />
          <main className="flex-1 overflow-y-auto p-6">
            <Outlet />
          </main>
        </div>
      </div>
      <RoiCalculatorModal open={roiCalculatorOpen} onClose={closeRoiCalculator} />
    </SalesEnablementContext.Provider>
  )
}
